<script lang="ts" setup>
    import { type FormInputs, getFormInput } from '~/utils/formUtils';
    import type { FormInputStoryblok } from '~/.storyblok/types/FormInputStoryblok';

    type RenderedInputBlok = Omit<FormInputStoryblok, 'inputType'> & {
        inputType: FormInputStoryblok['inputType'] | 'tel' | 'date';
    };

    const props = defineProps<{ blok: RenderedInputBlok; isConsent?: boolean }>();
    const formInputs = inject<Ref<FormInputs>>('formInputs')!;

    const validationErrors = computed(() => formInputs?.value[props.blok.inputKey]?.errors ?? []);

    const setInputValue = (newVal: string | number | boolean | undefined) => {
        const input = formInputs.value[props.blok.inputKey];
        if (!input || typeof newVal === 'undefined') return;
        input.value = typeof newVal === 'number' ? String(newVal) : newVal;
    };
    const inputValue = computed<string>({
        get: () => {
            const value = getFormInput(formInputs?.value, props.blok.inputKey);
            return typeof value === 'string' ? value : '';
        },
        set: setInputValue
    });
    const checkboxInputValue = computed<boolean>({
        get: () => getFormInput(formInputs?.value, props.blok.inputKey) === true,
        set: setInputValue
    });
</script>

<template>
    <div
        v-if="props.blok.inputType === 'checkbox'"
        v-editable="blok"
    >
        <div
            class="form-element --checkbox"
            :class="{ '--has-error': validationErrors?.length > 0 }"
        >
            <label
                :for="props.blok._uid"
                :class="{ '--consent': props.isConsent }"
            >
                <span v-if="props.blok.required && props.blok.revertAsterisk">* </span>
                <RichText :doc="props.blok.checkboxLabel" />
                <span v-if="props.blok.required && !props.blok.revertAsterisk">*</span>
            </label>
            <input
                :id="props.blok._uid"
                v-model="checkboxInputValue"
                :aria-invalid="validationErrors?.length > 0"
                :aria-describedby="validationErrors?.length > 0 ? `error-${props.blok._uid}` : undefined"
                type="checkbox"
                :required="props.blok.required"
                :name="props.blok.inputKey"
            />
        </div>

        <FormErrors
            :id="props.blok._uid"
            :validation-errors="validationErrors"
        />
    </div>

    <div
        v-else
        class="form-element"
        :class="{ '--has-error': validationErrors?.length > 0 }"
    >
        <label
            v-if="props.blok.inputType !== 'hidden'"
            :for="props.blok._uid"
        >
            {{ props.blok.label }}<span>{{ props.blok.required ? '*' : '' }}</span>
        </label>

        <textarea
            v-if="props.blok.inputType === 'textbox'"
            :id="props.blok._uid"
            v-model="inputValue"
            :aria-invalid="validationErrors?.length > 0"
            :aria-describedby="validationErrors?.length > 0 ? `error-${props.blok._uid}` : undefined"
            :name="props.blok.inputKey"
            :placeholder="props.blok.placeholderText"
            :required="props.blok.required"
        />
        <input
            v-else
            :id="props.blok._uid"
            v-model="inputValue"
            :aria-invalid="validationErrors?.length > 0"
            :aria-describedby="validationErrors?.length > 0 ? `error-${props.blok._uid}` : undefined"
            :type="props.blok.inputType"
            :required="props.blok.required"
            :name="props.blok.inputKey"
            :placeholder="props.blok.placeholderText"
        />
        <FormErrors
            v-if="props.blok.inputType !== 'hidden'"
            :id="props.blok._uid"
            :validation-errors="validationErrors"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/form/form-input.scss';
</style>
